import type { UseCaseEntry } from "@regpulse/contracts";

/**
 * U · Use Case Intelligence seed catalog (A1 / ADR-0008 / RFC-0001).
 *
 * Honest landscape: only declared-scope use-cases (mapped to the v0.1 EU/UNECE
 * complete core — REG-TA-001 / REG-GSR-001 / REG-GSR-002 / REG-PV-001 /
 * REG-CS-001 / REG-CS-002) carry coverageStatus complete/partial +
 * canonicalRuleIds. Everything else is planned / out_of_scope_v0_1 and is shown
 * at its TRUE status — never claimed covered.
 *
 * `publicCaseSignal` is intentionally absent: public case / enforcement signals
 * are sourced + human-verified via the discovery→review pipeline (RFC-0001
 * Track B2/B3) before display, so the seed adds none (no unsourced claims). The
 * schema + UI support them once verified. regulationClusters are descriptive
 * landscape labels, not canonical legal conclusions.
 */
export const USE_CASE_CATALOG: UseCaseEntry[] = [
  // — declared-scope core (complete: maps to a proven-complete rule) —
  {
    id: "uc-type-approval",
    useCase: "Whole-vehicle type approval (WVTA)",
    regulationClusters: ["Regulation (EU) 2018/858"],
    keyIssues: ["EU type-approval framework", "conformity of production"],
    coverageStatus: "complete",
    canonicalRuleIds: ["REG-TA-001"],
    lifecycleState: "ACTIVE",
  },
  {
    id: "uc-isa",
    useCase: "Intelligent Speed Assistance (ISA)",
    regulationClusters: ["Regulation (EU) 2021/1958 (GSR / ISA)"],
    keyIssues: ["ISA mandatory equipment", "warning strategy"],
    coverageStatus: "complete",
    canonicalRuleIds: ["REG-GSR-002"],
    lifecycleState: "ACTIVE",
  },
  {
    id: "uc-csms",
    useCase: "Cybersecurity management system (CSMS)",
    regulationClusters: ["UN R155"],
    keyIssues: ["CSMS certificate", "threat / risk assessment", "incident handling"],
    coverageStatus: "complete",
    canonicalRuleIds: ["REG-CS-001"],
    lifecycleState: "ACTIVE",
  },
  {
    id: "uc-sums-ota",
    useCase: "OTA / software update management (SUMS)",
    regulationClusters: ["UN R156"],
    keyIssues: ["RXSWIN", "rollback", "type-approval impact of updates"],
    coverageStatus: "complete",
    canonicalRuleIds: ["REG-CS-002"],
    lifecycleState: "ACTIVE",
  },
  // — declared-backed but broader (partial) —
  {
    id: "uc-gsr-safety",
    useCase: "General Safety mandated systems (AEB / ESC / DMS baseline)",
    regulationClusters: ["Regulation (EU) 2019/2144 (GSR)"],
    keyIssues: ["mandated active-safety systems", "driver drowsiness / attention (DMS)"],
    coverageStatus: "partial",
    canonicalRuleIds: ["REG-GSR-001"],
    lifecycleState: "ACTIVE",
  },
  {
    id: "uc-privacy",
    useCase: "In-vehicle personal-data processing",
    regulationClusters: ["GDPR 2016/679"],
    keyIssues: ["lawful basis", "data-subject rights", "in-vehicle telematics data"],
    coverageStatus: "partial",
    canonicalRuleIds: ["REG-PV-001"],
    lifecycleState: "ACTIVE",
  },
  {
    id: "uc-l2-adas",
    useCase: "L2 ADAS baseline (AEB / ESC / ISA)",
    regulationClusters: ["GSR 2019/2144", "GSR / ISA 2021/1958", "UN R152"],
    keyIssues: ["mandated assist systems", "UN R152 AEB (beyond v0.1)"],
    coverageStatus: "partial",
    canonicalRuleIds: ["REG-GSR-001", "REG-GSR-002"],
    lifecycleState: "SEED_UNVERIFIED",
  },
  // — planned (cluster in-force but not declared-complete in v0.1) —
  {
    id: "uc-dcas",
    useCase: "L2+ / DCAS / highway pilot",
    regulationClusters: ["UN R171 (DCAS)", "UN R155", "AI Act 2024/1689"],
    keyIssues: ["driver takeover", "event logging", "incident attribution", "AI classification"],
    coverageStatus: "planned",
    canonicalRuleIds: [],
    lifecycleState: "PLACEHOLDER",
  },
  {
    id: "uc-l3-alks",
    useCase: "L3 ALKS (automated lane keeping)",
    regulationClusters: ["UN R157", "UN R155", "AI Act 2024/1689"],
    keyIssues: ["liability transfer", "ODD boundaries"],
    coverageStatus: "planned",
    canonicalRuleIds: [],
    lifecycleState: "PLACEHOLDER",
  },
  {
    id: "uc-parking-rpa",
    useCase: "Parking assist / remote parking (RPA)",
    regulationClusters: ["UN R79", "UN R157 amendments"],
    keyIssues: ["remote activation", "supervisory control"],
    coverageStatus: "planned",
    canonicalRuleIds: [],
    lifecycleState: "PLACEHOLDER",
  },
  {
    id: "uc-remote-diagnostics",
    useCase: "Remote diagnostics / connected services",
    regulationClusters: ["Data Act 2023/2854", "GDPR 2016/679"],
    keyIssues: ["data access", "third-party sharing", "data-holder identity"],
    coverageStatus: "planned",
    canonicalRuleIds: [],
    lifecycleState: "PLACEHOLDER",
  },
  {
    id: "uc-rmi-aftersales",
    useCase: "Aftersales / independent repair access (RMI)",
    regulationClusters: ["Regulation (EU) 2018/858 (RMI)", "competition law"],
    keyIssues: ["independent-operator access", "RMI data"],
    coverageStatus: "planned",
    canonicalRuleIds: [],
    lifecycleState: "PLACEHOLDER",
  },
  {
    id: "uc-insurance-ubi",
    useCase: "Insurance telematics / UBI",
    regulationClusters: ["GDPR 2016/679", "Data Act 2023/2854"],
    keyIssues: ["data-holder identity", "consent", "telematics data"],
    coverageStatus: "planned",
    canonicalRuleIds: [],
    lifecycleState: "PLACEHOLDER",
  },
  {
    id: "uc-fleet-leasing",
    useCase: "Fleet management / leasing",
    regulationClusters: ["Data Act 2023/2854", "GDPR 2016/679"],
    keyIssues: ["user vs fleet-owner data rights"],
    coverageStatus: "planned",
    canonicalRuleIds: [],
    lifecycleState: "PLACEHOLDER",
  },
  {
    id: "uc-digital-key",
    useCase: "Digital key",
    regulationClusters: ["CCC Digital Key", "UN R155"],
    keyIssues: ["security protocol", "key provisioning"],
    coverageStatus: "planned",
    canonicalRuleIds: [],
    lifecycleState: "PLACEHOLDER",
  },
  // — out of scope for v0.1 (tracked, not covered) —
  {
    id: "uc-v2x",
    useCase: "V2X / C-V2X (5G-V2X, spectrum, cross-border roaming)",
    regulationClusters: ["UN R10 (EMC)", "national spectrum"],
    keyIssues: ["frequency band", "cross-border roaming"],
    coverageStatus: "out_of_scope_v0_1",
    canonicalRuleIds: [],
    lifecycleState: "PLACEHOLDER",
  },
  {
    id: "uc-hd-map",
    useCase: "HD map / location service",
    regulationClusters: ["member-state surveying law", "GDPR location data"],
    keyIssues: ["surveying qualification", "data ownership", "CN↔EU data flow"],
    coverageStatus: "out_of_scope_v0_1",
    canonicalRuleIds: [],
    lifecycleState: "PLACEHOLDER",
  },
  {
    id: "uc-app-store",
    useCase: "In-vehicle app store / mini-programs",
    regulationClusters: ["Digital Services Act", "AI Act 2024/1689", "GDPR 2016/679"],
    keyIssues: ["content moderation (if VLOP)", "third-party apps"],
    coverageStatus: "out_of_scope_v0_1",
    canonicalRuleIds: [],
    lifecycleState: "PLACEHOLDER",
  },
  {
    id: "uc-genai-assistant",
    useCase: "Voice assistant / generative AI",
    regulationClusters: ["AI Act 2024/1689 (transparency)", "GDPR 2016/679"],
    keyIssues: ["AI transparency labelling", "training-data provenance"],
    coverageStatus: "out_of_scope_v0_1",
    canonicalRuleIds: [],
    lifecycleState: "PLACEHOLDER",
  },
  {
    id: "uc-charging-interop",
    useCase: "Charging network interoperability",
    regulationClusters: ["ISO 15118", "OCPP", "NIS2"],
    keyIssues: ["plug-and-charge", "critical infrastructure"],
    coverageStatus: "out_of_scope_v0_1",
    canonicalRuleIds: [],
    lifecycleState: "PLACEHOLDER",
  },
  {
    id: "uc-cross-border-data",
    useCase: "Cross-border data transfer (CN ↔ EU)",
    regulationClusters: ["CN data-export rules", "GDPR SCC", "Data Act 2023/2854"],
    keyIssues: ["bidirectional transfer compliance"],
    coverageStatus: "out_of_scope_v0_1",
    canonicalRuleIds: [],
    lifecycleState: "PLACEHOLDER",
  },
];
